import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { DONT_NEED } from "constants/cartItemDeletionReasons";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useMarkAsNotRequiredMutation } from "store/apis/filing";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";

export const MarkFilingAsNotRequired = ({
  isOpen,
  close,
  taskDeadline,
}: {
  isOpen: boolean;
  close: () => void;
  taskDeadline: TaskDeadlineOverviewItem;
}) => {
  const { uuid: groupId, secondary_channel_url } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();

  const [markAsNotRequired, { isLoading }] = useMarkAsNotRequiredMutation();

  const markFilingAsnotRequired = async () => {
    try {
      if (
        taskDeadline.product_content_type_id &&
        taskDeadline.base_task_template.uuid
      ) {
        const cartItemId = taskDeadline.cart_item_uuid
          ? { cart_item_id: taskDeadline.cart_item_uuid }
          : {};
        const stateId = taskDeadline.alarm_state_id
          ? { state_id: taskDeadline.alarm_state_id }
          : {};
        await markAsNotRequired({
          entityId: taskDeadline.entity.uuid,
          groupId,
          payload: {
            product_content_type_id: taskDeadline.product_content_type_id,
            season: taskDeadline.season,
            product_id: taskDeadline.base_task_template.uuid,
            delete_reason: DONT_NEED,
            payment_amount:
              !taskDeadline.payment_amount ||
              isNaN(Number(taskDeadline.payment_amount))
                ? 0
                : Number(taskDeadline.payment_amount),
            ...cartItemId,
            ...stateId,
          },
        }).unwrap();
        successToast({
          title: "Filing updated",
          message: "Filing status has been updated to ‘Not required’",
        });
        close();
      }
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small">
        <Modal.Body>
          <div>
            <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
              <TriangleWarning />
            </span>
            <p className="t-mb-2 t-text-subtext t-text-text-100 t-mt-5">
              Mark filing as not required
            </p>
            <div className="t-text-body-sm t-text-text-30">
              Are you sure you want to mark this filing as not required? If in
              doubt, check with the team before updating.
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button
            onClick={() =>
              openLink(
                `/tax/chat/${
                  taskDeadline.channel?.channel_url ??
                  secondary_channel_url ??
                  ""
                }`
              )
            }
          >
            Chat with us
          </Button>
          <Button
            isLoading={isLoading}
            disabled={isLoading}
            customType="primary"
            onClick={markFilingAsnotRequired}
          >
            Mark as not required
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
